import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { AuthShell } from "./Login";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If that email exists, a reset link has been sent");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, "Could not send reset email"));
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <AuthShell>
        <div className="overline mb-2">SITESYNC / CHECK YOUR INBOX</div>
        <h1 className="text-3xl font-semibold tracking-tighter mb-6">Reset link sent.</h1>
        <div className="border border-border bg-card rounded-sm p-5 flex items-start gap-3">
          <MailCheck className="h-5 w-5 text-primary mt-0.5" />
          <div className="text-sm">
            If an account exists for <strong className="font-mono">{email}</strong>, a reset link is on its way.
            The link expires in <strong>30 minutes</strong>.
            <div className="mt-2 text-xs text-muted-foreground font-mono">
              Didn't receive it? Check your spam folder, or wait a minute before requesting again (max 5/hour).
            </div>
          </div>
        </div>
        <div className="mt-6 flex gap-2">
          <Link to="/login"><Button variant="outline" className="rounded-sm border-border" data-testid="forgot-back-to-login">← Back to login</Button></Link>
          <Button variant="ghost" className="rounded-sm" onClick={() => { setSent(false); setEmail(""); }} data-testid="forgot-try-again">Try another email</Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="overline mb-2">SITESYNC / PASSWORD RESET</div>
      <h1 className="text-3xl font-semibold tracking-tighter mb-8">Forgot your password?</h1>
      <p className="text-sm text-muted-foreground mb-6 max-w-sm">
        Enter the email you use for SiteSync and we'll send a link to reset your password.
      </p>
      <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
        <div>
          <Label className="overline mb-2 block">Email</Label>
          <Input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            className="rounded-sm font-mono"
            data-testid="forgot-email-input"
          />
        </div>
        {err && (
          <div className="text-xs text-destructive font-mono border border-destructive/40 bg-destructive/5 px-3 py-2 rounded-sm" data-testid="forgot-error">
            {err}
          </div>
        )}
        <Button type="submit" disabled={busy} className="w-full rounded-sm h-11 bg-primary hover:bg-primary/90" data-testid="forgot-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
        </Button>
      </form>
      <div className="mt-8 border-t border-border pt-6 text-sm text-muted-foreground">
        Remembered it? <Link to="/login" className="text-foreground hover:text-primary">Sign in</Link>
      </div>
    </AuthShell>
  );
}
